// src/app/slice/invoiceSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import invoiceService from "../../features/invoice/service/invoiceService";
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
} from "../../features/invoice/types/invoiceTypes";

// Async Thunks
export const fetchInvoices = createAsyncThunk(
  "invoice/fetchInvoices",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getInvoices(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const fetchInvoiceById = createAsyncThunk(
  "invoice/fetchInvoiceById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getInvoiceById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

export const createInvoice = createAsyncThunk(
  "invoice/createInvoice",
  async (invoiceData, { rejectWithValue }) => {
    try {
      const response = await invoiceService.createInvoice(invoiceData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.CREATE_FAILED);
    }
  },
);

export const updateInvoice = createAsyncThunk(
  "invoice/updateInvoice",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await invoiceService.updateInvoice(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.UPDATE_FAILED);
    }
  },
);

export const deleteInvoice = createAsyncThunk(
  "invoice/deleteInvoice",
  async (id, { rejectWithValue }) => {
    try {
      await invoiceService.deleteInvoice(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.DELETE_FAILED);
    }
  },
);

export const updateInvoiceStatus = createAsyncThunk(
  "invoice/updateInvoiceStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await invoiceService.updateStatus(id, status);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || ERROR_MESSAGES.STATUS_UPDATE_FAILED,
      );
    }
  },
);

export const sendInvoice = createAsyncThunk(
  "invoice/sendInvoice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await invoiceService.sendInvoice(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.SEND_FAILED);
    }
  },
);

export const recordPayment = createAsyncThunk(
  "invoice/recordPayment",
  async ({ id, paymentData }, { rejectWithValue }) => {
    try {
      const response = await invoiceService.recordPayment(id, paymentData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.PAYMENT_FAILED);
    }
  },
);

export const fetchInvoiceStats = createAsyncThunk(
  "invoice/fetchInvoiceStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await invoiceService.getInvoiceStats();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || ERROR_MESSAGES.FETCH_FAILED);
    }
  },
);

const initialState = {
  invoices: [],
  selectedInvoice: null,
  loading: false,
  error: null,
  success: null,
  filters: {
    status: "all",
    search: "",
    dateRange: { start: null, end: null },
    clientId: "all",
    minAmount: null,
    maxAmount: null,
    paymentMethod: "all",
  },
  sortBy: "date",
  sortOrder: "desc",
  stats: {
    total: 0,
    draft: 0,
    sent: 0,
    viewed: 0,
    paid: 0,
    partially_paid: 0,
    overdue: 0,
    cancelled: 0,
    refunded: 0,
    totalValue: 0,
    totalPaid: 0,
    totalOutstanding: 0,
    averageValue: 0,
    collectionRate: 0,
    overdueCount: 0,
  },
};

const invoiceSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    setInvoices: (state, action) => {
      state.invoices = action.payload;
    },
    setSelectedInvoice: (state, action) => {
      state.selectedInvoice = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setSort: (state, action) => {
      state.sortBy = action.payload.sortBy || state.sortBy;
      state.sortOrder = action.payload.sortOrder || state.sortOrder;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    resetInvoiceState: (state) => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload;
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchInvoiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedInvoice = action.payload;
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices.unshift(action.payload);
        state.selectedInvoice = action.payload;
        state.success = SUCCESS_MESSAGES.CREATE_SUCCESS;
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.invoices.findIndex(
          (inv) => inv.id === action.payload.id,
        );
        if (index !== -1) {
          state.invoices[index] = action.payload;
        }
        state.selectedInvoice = action.payload;
        state.success = SUCCESS_MESSAGES.UPDATE_SUCCESS;
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = state.invoices.filter(
          (inv) => inv.id !== action.payload,
        );
        if (state.selectedInvoice?.id === action.payload) {
          state.selectedInvoice = null;
        }
        state.success = SUCCESS_MESSAGES.DELETE_SUCCESS;
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateInvoiceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateInvoiceStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.invoices.findIndex(
          (inv) => inv.id === action.payload.id,
        );
        if (index !== -1) {
          state.invoices[index] = action.payload;
        }
        if (state.selectedInvoice?.id === action.payload.id) {
          state.selectedInvoice = action.payload;
        }
        state.success = SUCCESS_MESSAGES.STATUS_UPDATE_SUCCESS;
      })
      .addCase(updateInvoiceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(sendInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(sendInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.invoices.findIndex(
          (inv) => inv.id === action.payload.id,
        );
        if (index !== -1) {
          state.invoices[index] = action.payload;
        }
        if (state.selectedInvoice?.id === action.payload.id) {
          state.selectedInvoice = action.payload;
        }
        state.success = SUCCESS_MESSAGES.SEND_SUCCESS;
      })
      .addCase(sendInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(recordPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(recordPayment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.invoices.findIndex(
          (inv) => inv.id === action.payload.id,
        );
        if (index !== -1) {
          state.invoices[index] = action.payload;
        }
        if (state.selectedInvoice?.id === action.payload.id) {
          state.selectedInvoice = action.payload;
        }
        state.success = SUCCESS_MESSAGES.PAYMENT_SUCCESS;
      })
      .addCase(recordPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchInvoiceStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchInvoiceStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setInvoices,
  setSelectedInvoice,
  setFilters,
  clearFilters,
  setSort,
  clearError,
  clearSuccess,
  resetInvoiceState,
} = invoiceSlice.actions;

export default invoiceSlice.reducer;
